import {
	BCInputNumber,
	BCInputNumberRef,
	IInputNumberProps,
	NumberFormatter
} from '@bimser/components';
import { FormItemTypes, IDictionary, MLHelper, isNullOrUndefined } from '@bimser/core';
import React, { JSX } from 'react';
import { IPropertyField, SizeType } from '../../../../entities/common';
import EditControl from '../../base/EditControl';
import Styles from './assets/style.scss';
import { INumberBoxProperties } from './entities/INumberBoxProperties';

export default class NumberBox extends EditControl<number> {
	numFormatter: NumberFormatter;

	_decimalSeparator: string = '';
	_thousandsSeparator: string = '';

	max: number;
	min: number;
	step: number;
	useThousandSeparator: boolean;
	precision: number;
	sizeType: SizeType;
	inputRef = React.createRef<BCInputNumberRef>();

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.NumberBox;
		this.useThousandSeparator = false;
		this.precision = 0;
		this.step = 1;
		this.max = Number.MAX_SAFE_INTEGER;

		const delimiters = MLHelper.numberVariables.delimiters;

		this._decimalSeparator = delimiters.decimal;
		this._thousandsSeparator = delimiters.thousands;

		this.numFormatter = new NumberFormatter();

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			const _value = Number(value);

			if (Number.isNaN(_value) && !isNullOrUndefined(value)) {
				return;
			} else if (isNullOrUndefined(value)) {
				this.triggerValueChanged(null);
			} else if ((this.min || -Infinity) <= _value && _value <= (this.max || Infinity)) {
				this.triggerValueChanged(value === '' ? null : _value);
			} else if ((this.min || -Infinity) > _value) {
				this.triggerValueChanged(this.min || 0);
			} else if (_value > this.max) {
				this.triggerValueChanged(this.max);
			}
		});

		this.subscribe('trigger:OnIncreaseNumberBoxValue', (value: string) => {
			this.inputRef?.current?.triggerStep(true, Number(value));
		});

		this.subscribe('trigger:OnDecreaseNumberBoxValue', (value: string) => {
			this.inputRef?.current?.triggerStep(false, Number(value));
		});
	}

	initialize() {
		super.initialize();
		this.textAlign = 'right';
	}

	__loadProperties(data: INumberBoxProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.max = data.max;
		this.__proxyChanged.min = data.min;
		this.__proxyChanged.useThousandSeparator = data.useThousandSeparator;
		this.__proxyChanged.step = data.step;
		this.__proxyChanged.precision = data.precision;
		this.__proxyChanged.sizeType = data.sizeType;

		this.numFormatter.init(data.precision, data.useThousandSeparator || false);
	}

	protected triggerValueChanged(value: number) {
		this.__proxyChanging.value = value;
	}

	focus() {
		this.inputRef.current?.ref?.focus();
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'value',
				type: 'number',
				common: true
			},
			{
				name: 'max',
				type: 'number'
			},
			{
				name: 'min',
				type: 'number'
			},
			{
				name: 'step',
				type: 'number'
			},
			{
				name: 'useThousandSeparator',
				type: 'boolean'
			},
			{
				name: 'precision',
				type: 'number'
			}
		];
	}

	__getComponentData(): IInputNumberProps & IDictionary<any> {
		return {
			...super.__getComponentData(),
			value: this.value,
			max: this.max,
			min: this.min,
			step: this.step,
			useThousandSeparator: this.useThousandSeparator,
			precision: this.precision,
			sizeType: this.sizeType,
			_decimalSeparator: this._decimalSeparator,
			_thousandsSeparator: this._thousandsSeparator,
			disabled: this.__isDisabled()
		};
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<BCInputNumber
				ref={this.inputRef}
				value={this.value}
				readOnly={this.__isReadOnly()}
				placeholder={this.placeholder.getText()}
				disabled={this.__isDisabled()}
				tabIndex={this.tabIndex}
				max={this.max}
				min={this.min}
				precision={this.precision}
				step={this.step}
				style={{ width: '100%', ...this.style }}
				className={Styles.transparentContent}
				onChange={this.triggerValueChanged.bind(this)}
				size={this.__getSizeType()}
				useThousandSeparator={this.useThousandSeparator}
				textAlign={this.textAlign}
				mobileControls
			/>
		);
	}
}
